import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ReviewPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="container-main pt-24 pb-16">
        <div className="max-w-3xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6">
            <a href="/" className="hover:text-primary-600 transition-colors">Home</a>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-600">AllChinaBuy Review</span>
          </nav>

          <span className="inline-block px-2.5 py-1 rounded text-xs font-medium bg-accent-100 text-accent-700 mb-3">Review</span>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4">
            AllChinaBuy Review: Features & Buying Experience 2026
          </h1>
          <div className="flex items-center gap-4 text-sm text-foreground-400 mb-8">
            <span className="flex items-center gap-1"><i className="ri-calendar-line" /> Updated August 25, 2026</span>
            <span className="flex items-center gap-1"><i className="ri-time-line" /> 9 min read</span>
          </div>

          <div className="rounded-lg overflow-hidden mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=Person%20reviewing%20products%20on%20modern%20laptop%20at%20minimal%20white%20desk%2C%20coffee%20cup%20and%20notebook%20nearby%2C%20warm%20natural%20lighting%20from%20window%2C%20editorial%20photography%20style%2C%20soft%20beige%20and%20cream%20tones%2C%20product%20review%20and%20research%20concept%2C%20professional%20aesthetic&width=1200&height=600&seq=article-review-hero-001&orientation=landscape"
              alt="AllChinaBuy Review"
              className="w-full h-64 md:h-80 object-cover"
            />
          </div>

          <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
            <p className="text-base mb-6">
              This is an independent, honest <strong>AllChinaBuy review</strong> based on platform features, buyer experience, and our research. We are not affiliated with AllChinaBuy and this review reflects our independent assessment of the platform's strengths and weaknesses.
            </p>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">Platform Overview</h2>
            <p className="text-base mb-4">
              AllChinaBuy operates as a shopping agent that bridges international buyers with Chinese e-commerce sellers. The platform handles order placement, payment, quality control, and shipping logistics. This model is common in the global replica and fashion community, where buyers seek products that may not be available through traditional retail channels.
            </p>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">Key Features Assessment</h2>

            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm border border-background-200/70 rounded-lg">
                <thead>
                  <tr className="bg-background-100">
                    <th className="px-4 py-3 text-left font-medium text-foreground-700">Feature</th>
                    <th className="px-4 py-3 text-left font-medium text-foreground-700">Rating</th>
                    <th className="px-4 py-3 text-left font-medium text-foreground-700">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/50">
                  <tr>
                    <td className="px-4 py-3 font-medium">Product Search</td>
                    <td className="px-4 py-3"><span className="text-accent-600">Good</span></td>
                    <td className="px-4 py-3 text-foreground-600">Integration with Chinese marketplaces; search by link or keywords</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">QC Photos</td>
                    <td className="px-4 py-3"><span className="text-accent-600">Good</span></td>
                    <td className="px-4 py-3 text-foreground-600">Multiple angles provided; quality varies by warehouse</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Shipping Options</td>
                    <td className="px-4 py-3"><span className="text-accent-600">Good</span></td>
                    <td className="px-4 py-3 text-foreground-600">Multiple carriers available; reasonable international coverage</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Customer Support</td>
                    <td className="px-4 py-3"><span className="text-secondary-600">Fair</span></td>
                    <td className="px-4 py-3 text-foreground-600">Response times vary; language barrier can be an issue</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Website UX</td>
                    <td className="px-4 py-3"><span className="text-accent-600">Good</span></td>
                    <td className="px-4 py-3 text-foreground-600">Functional interface; mobile experience is acceptable</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Payment Options</td>
                    <td className="px-4 py-3"><span className="text-accent-600">Good</span></td>
                    <td className="px-4 py-3 text-foreground-600">Multiple methods accepted; PayPal available in some regions</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">Pros & Cons</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-accent-50 rounded-lg p-4 border border-accent-200/50">
                <p className="text-sm font-medium text-accent-800 mb-3">Pros</p>
                <ul className="space-y-2 text-sm text-accent-700">
                  <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5" /> Wide product selection through marketplace integration</li>
                  <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5" /> QC photos help verify product quality before shipping</li>
                  <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5" /> Multiple shipping options for different budgets</li>
                  <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5" /> Escrow payment provides buyer protection</li>
                  <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5" /> Consolidation saves on shipping costs</li>
                </ul>
              </div>
              <div className="bg-primary-50 rounded-lg p-4 border border-primary-200/50">
                <p className="text-sm font-medium text-primary-800 mb-3">Cons</p>
                <ul className="space-y-2 text-sm text-primary-700">
                  <li className="flex items-start gap-2"><i className="ri-close-line text-primary-500 mt-0.5" /> Customer service response times can be slow</li>
                  <li className="flex items-start gap-2"><i className="ri-close-line text-primary-500 mt-0.5" /> Language barrier with some sellers</li>
                  <li className="flex items-start gap-2"><i className="ri-close-line text-primary-500 mt-0.5" /> Shipping costs can be high for heavy items</li>
                  <li className="flex items-start gap-2"><i className="ri-close-line text-primary-500 mt-0.5" /> No guaranteed delivery timeframes</li>
                  <li className="flex items-start gap-2"><i className="ri-close-line text-primary-500 mt-0.5" /> Customs risk is buyer's responsibility</li>
                </ul>
              </div>
            </div>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">The Buying Experience</h2>
            <p className="text-base mb-4">
              The typical AllChinaBuy buying experience follows this flow: search for products, add to cart, submit order, wait for seller to ship to warehouse, review QC photos, approve or reject, select shipping method, and wait for delivery. The entire process typically takes 2-5 weeks depending on shipping choice.
            </p>
            <p className="text-base mb-6">
              The QC photo stage is where most buyers make critical decisions. These photos show the actual items that arrived at the warehouse, allowing you to verify quality before committing to international shipping. Rejecting flawed items at this stage saves money on shipping defective products.
            </p>

            <div className="bg-accent-50 rounded-lg p-5 border border-accent-200/50 mb-6">
              <p className="text-sm text-accent-800 font-medium mb-2">Independent Verdict</p>
              <p className="text-sm text-accent-700 leading-relaxed">
                AllChinaBuy offers a functional shopping agent experience with reasonable features for its market. The QC process and escrow payment are genuine strengths. However, buyers should set realistic expectations around shipping times, customer service responsiveness, and the inherent risks of cross-border e-commerce.
              </p>
            </div>

            <p className="text-base mb-6">
              Read more in our <a href="/allchinabuy-spreadsheet-complete-guide" className="text-primary-600 hover:underline">Complete Guide</a> or <a href="/is-allchinabuy-legit" className="text-primary-600 hover:underline">Safety Guide</a>.
            </p>

            <div className="mt-10 bg-primary-50 rounded-lg p-6 md:p-8 text-center border border-primary-200/50">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Try It Yourself</h3>
              <p className="text-sm text-foreground-600 mb-4">Visit the main website to explore products and see the platform in action.</p>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="btn-primary inline-flex">
                Visit AllChinaBuy Spreadsheet
                <i className="ri-external-link-line ml-1" />
              </a>
            </div>

            <div className="mt-10 pt-6 border-t border-background-200/50">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">Related Articles</p>
              <div className="flex flex-wrap gap-2">
                <a href="/allchinabuy-spreadsheet-complete-guide" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">Complete Guide</a>
                <a href="/is-allchinabuy-legit" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">Safety Guide</a>
                <a href="/allchinabuy-shipping-cost-guide" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">Shipping Guide</a>
                <a href="/allchinabuy-qc-guide" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">QC Guide</a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}